import { REST_BASE_URL_DEBIT_CARDS } from '@/react-query/common';

import { CardType, GetPinSession } from '@generated-types';
import { cardTypeGet, cardTypePost } from '@utils/query-utils';

async function hexToBytes(hex: string) {
    const bytes: number[] = [];
    for (let c = 0; c < hex.length; c += 2) bytes.push(parseInt(hex.substr(c, 2), 16));
    return bytes;
}

async function bytesToHex(buffer: ArrayBuffer) {
    let s = '';
    const h = '0123456789ABCDEF';
    new Uint8Array(buffer).forEach((v) => {
        // eslint-disable-next-line no-bitwise
        s += h[v >> 4] + h[v & 15];
    });
    return s;
}

const getPinSessionFromCardId = async (cardId: string) =>
    cardTypeGet(CardType.DEBIT)<GetPinSession>(`${REST_BASE_URL_DEBIT_CARDS}/${cardId}/pin-session`);

const postEncryptedPin = async (cardId: string, pinData: any) =>
    cardTypePost(CardType.DEBIT)<string>(`${REST_BASE_URL_DEBIT_CARDS}/${cardId}/encrypted-pin`, pinData);

const generateAESKey = async (): Promise<CryptoKey> => {
    // eslint-disable-next-line no-return-await
    return await window.crypto.subtle.generateKey(
        {
            name: 'AES-GCM',
            length: 128,
        },
        true,
        ['encrypt', 'decrypt'],
    );
};

const importPublicKey = async (publicKey: string): Promise<CryptoKey> => {
    const matsBinaryDer = Uint8Array.from(window.atob(publicKey), (c) => c.charCodeAt(0));

    // eslint-disable-next-line no-return-await
    return await crypto.subtle.importKey(
        'spki',
        matsBinaryDer,
        {
            name: 'RSA-OAEP',
            hash: 'SHA-256',
        },
        true,
        ['wrapKey'],
    );
};

const getPinData = async (cardId: string, aesKey: CryptoKey): Promise<string> => {
    const pinSession = await getPinSessionFromCardId(cardId);

    const pubKey = await importPublicKey(pinSession.publicKey);
    const aesWrappedKey = await window.crypto.subtle.wrapKey('raw', aesKey, pubKey, {
        name: 'RSA-OAEP',
    });

    const aesWrappedKeyHex = await bytesToHex(aesWrappedKey);

    const pinData = { sessionId: pinSession.sessionId, encryptedSecretKey: aesWrappedKeyHex };

    const encryptedPinResponse = await postEncryptedPin(cardId, pinData);

    return encryptedPinResponse;
};

export const decryptKey = async (cardId: string, encryptedPinBase64: string, aesKey: CryptoKey) => {
    const encryptedPin = await hexToBytes(encryptedPinBase64);

    const iv = encryptedPin.slice(0, 12);
    const aad = Uint8Array.from(cardId.split('').map((letter) => letter.charCodeAt(0)));
    const pin = await crypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv: Uint8Array.from(iv).buffer,
            tagLength: 128,
            additionalData: aad,
        },
        aesKey,
        Uint8Array.from(encryptedPin.slice(12)).buffer,
    );

    return new TextDecoder().decode(pin);
};

export const getDebitCardPin = async (cardId: string) => {
    const aesKey = await generateAESKey();
    const pinData = await getPinData(cardId, aesKey);

    // eslint-disable-next-line no-return-await
    return await decryptKey(cardId, pinData, aesKey);
};
